/**
 * 🎰 开奖后筹码结算动画
 * 
 * 三种场景：
 * 1. 庄家通吃：有下注区域的筹码 → 庄家中心点 → 销毁
 * 2. 庄家通赔：庄家筹码 → 有下注的区域（数量=下注额×倍数）→ 在线玩家 → 销毁
 * 3. 部分区域赢：输家筹码 → 庄家 → 赢家区域（数量=赢家下注额×倍数）→ 在线玩家 → 销毁
 * 
 * 核心规则：
 * - 只有有下注的区域才参与结算
 * - 没有下注的区域即使赢了也不会分到筹码
 * - 筹码数量由下注额和倍数决定
 */

import { loadGSAP } from './cardAnimation';
import { splitAmountToChips } from './bettingAnimation';

// 🚩 退出标志，用于切换房间时阻止动画回调继续执行
let isSettlementExiting = false;

// 🎬 当前正在运行的 GSAP 动画实例
let activeSettlementTweens: any[] = [];

// ============================================
// ⚙️ 动画配置
// ============================================

const CONFIG = {
  // 🔧 优化：增大筹码尺寸，让动画更清晰可见
  CHIP_SIZE: 18.8,
  // 层级 - 高于 .pocker(z-index:1) 低于 .betting-result(z-index:66/4)
  // HallCover: .pocker(1) < chips(3) < .betting-result(66)
  // IndoorHeader: .pocker-container(3) ≤ chips(3) < .betting-result(4)
  Z_INDEX: 3,
  Z_INDEX_DESTROY: 3,
  // 🔧 优化：加快动画速度，使动画更顺滑
  DURATION_COLLECT: 0.4,      // 回收动画（筹码飞向目标）
  DURATION_DISTRIBUTE: 0.25,  // 分发动画（筹码从庄家飞出）
  DURATION_DESTROY: 0.4,      // 销毁动画（筹码飞向在线玩家）
  // 🔧 优化：减小间隔，让筹码更紧凑地飞行
  STAGGER: 0.015,             // 筹码间隔
  // 🚨 防超时：限制 stagger 总时间，大量筹码时动态缩小间隔
  MAX_STAGGER_TIME: 0.3,      // stagger 最大总时间（秒），让所有筹码快速飞完
  DELAY_BEFORE_DESTROY: 200,  // 分发后等待销毁的时间(ms)
} as const;

/**
 * 🎯 动态计算 stagger 值，防止大量筹码时动画超时
 * @param chipCount 筹码数量
 * @returns 实际使用的 stagger 值
 */
const getDynamicStagger = (chipCount: number): number => {
  if (chipCount <= 0) return CONFIG.STAGGER;
  // 计算：如果按默认 stagger 会超时，则动态缩小
  const maxAllowedStagger = CONFIG.MAX_STAGGER_TIME / chipCount;
  return Math.min(CONFIG.STAGGER, maxAllowedStagger);
};

// ============================================
// 📦 类型定义
// ============================================

interface ChipInfo {
  element: HTMLElement;
  centerX: number;
  centerY: number;
}

interface TargetPoint {
  x: number;
  y: number;
}

/**
 * 区域下注信息
 */
export interface AreaBetInfo {
  areaId: string;       // 区域ID: tian, di, xuan, huang
  totalBet: number;     // 该区域总下注额
  multiplier: number;   // 该区域牌型倍数
}

/**
 * 结算动画参数
 */
export interface SettlementParams {
  winningSectors: string[];    // 获胜区域数组
  areaBets: AreaBetInfo[];     // 各区域下注信息（包含下注额和倍数）
  minBetLimit?: number;        // 厅的最小下注限制
}

// ============================================
// 🔧 工具函数
// ============================================

/**
 * 获取元素的视觉中心点坐标
 */
function getElementCenter(selector: string): TargetPoint | null {
  const element = document.querySelector(selector);
  if (!element) return null;
  
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };
}

/**
 * 获取庄家最中心点
 * 优先取 .icon-banker 图标中心，确保精确
 */
function getBankerCenter(): TargetPoint | null {
  return getElementCenter('.banker-container .icon-banker') ||
         getElementCenter('.banker-container img') ||
         getElementCenter('.banker-container');
}

/**
 * 获取在线玩家按钮最中心点
 */
function getOnlinePlayerCenter(): TargetPoint | null {
  return getElementCenter('.operate.online') ||
         getElementCenter('.operate .icon-online') ||
         getElementCenter('.operate');
}

/**
 * 获取下注区域中心点
 */
function getBettingAreaCenter(areaId: string): TargetPoint | null {
  return getElementCenter(`.betting-area[data-area="${areaId}"]`);
}

/**
 * 收集指定区域的所有筹码信息（收集全部，不限流）
 * @param areaIds 区域ID数组
 */
function collectChipsFromAreas(areaIds: string[]): ChipInfo[] {
  const chips: ChipInfo[] = [];
  
  for (const areaId of areaIds) {
    const areaElement = document.querySelector(`.betting-area[data-area="${areaId}"]`);
    if (!areaElement) {
      console.log(`[结算动画] 区域 ${areaId} 未找到`);
      continue;
    }
    
    // 收集所有筹码类型的元素
    const chipSelectors = [
      '.permanent-chip-container',
      '.permanent-chip',
      '.other-player-permanent-chip',
      '.other-player-chip-container'
    ];
    
    for (const selector of chipSelectors) {
      const chipElements = areaElement.querySelectorAll(selector);
      // console.log(`[结算动画] 区域 ${areaId} 选择器 ${selector} 找到 ${chipElements.length} 个元素`);
      
      chipElements.forEach((chip) => {
        const rect = (chip as HTMLElement).getBoundingClientRect();
        // 只收集可见的筹码（宽高大于0）
        if (rect.width > 0 && rect.height > 0) {
          chips.push({
            element: chip as HTMLElement,
            centerX: rect.left + rect.width / 2,
            centerY: rect.top + rect.height / 2
          });
        }
      });
    }
  }
  
  console.log(`[结算动画] 总共收集到 ${chips.length} 个可见筹码`);
  return chips;
}

/**
 * 🚀 性能优化：将原始筹码转换为可动画的 fixed 定位
 * 直接移动原始筹码，不创建副本，减少一半Dom操作
 */
function convertToFixedPosition(chips: ChipInfo[]): HTMLElement[] {
  const elements: HTMLElement[] = [];
  
  for (const chip of chips) {
    const el = chip.element;
    // 🚀 直接修改原始筹码的定位方式
    Object.assign(el.style, {
      position: 'fixed',
      left: `${chip.centerX - CONFIG.CHIP_SIZE / 2}px`,
      top: `${chip.centerY - CONFIG.CHIP_SIZE / 2}px`,
      width: `${CONFIG.CHIP_SIZE}px`,
      height: `${CONFIG.CHIP_SIZE}px`,
      zIndex: CONFIG.Z_INDEX.toString(),
      pointerEvents: 'none',
      willChange: 'transform, opacity',
      transform: 'translateZ(0)',  // GPU加速
      margin: '0'
    });
    elements.push(el);
  }
  
  return elements;
}

/**
 * 创建从指定中心点出发的飞行筹码（用于分发动画）
 */
function createFlyingChipFromCenter(centerPoint: TargetPoint, templateChip: ChipInfo): HTMLElement {
  const flyingChip = document.createElement('div');
  flyingChip.className = 'settlement-flying-chip';
  
  // 克隆模板筹码的内容
  const clonedContent = templateChip.element.cloneNode(true) as HTMLElement;
  // 🔧 关键：重置克隆内容的所有定位相关样式，包括 opacity（原始筹码可能已被隐藏）
  Object.assign(clonedContent.style, {
    position: 'relative',
    left: '0',
    top: '0',
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
    margin: '0',
    width: '100%',
    height: '100%',
    opacity: '1',
    visibility: 'visible'
  });
  flyingChip.appendChild(clonedContent);
  
  // 初始位置 = 中心点 - 尺寸/2
  Object.assign(flyingChip.style, {
    position: 'fixed',
    left: `${centerPoint.x - CONFIG.CHIP_SIZE / 2}px`,
    top: `${centerPoint.y - CONFIG.CHIP_SIZE / 2}px`,
    width: `${CONFIG.CHIP_SIZE}px`,
    height: `${CONFIG.CHIP_SIZE}px`,
    zIndex: CONFIG.Z_INDEX.toString(),
    pointerEvents: 'none',
    willChange: 'left, top, transform',  // 🔧 GPU加速
    backfaceVisibility: 'hidden',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: '0', // 初始隐藏，动画开始时显示
    transform: 'translateZ(0)',  // 🔧 强制GPU渲染
    borderRadius: '50%'  // 🔧 筹码圆形效果
  });
  
  document.body.appendChild(flyingChip);
  return flyingChip;
}

/**
 * 创建默认样式的飞行筹码（当没有模板筹码时使用）
 */
function createDefaultFlyingChip(centerPoint: TargetPoint): HTMLElement {
  const flyingChip = document.createElement('div');
  flyingChip.className = 'settlement-flying-chip';
  
  // 创建默认筹码样式（黄色圆形）
  flyingChip.innerHTML = `<svg viewBox="0 0 100 100" width="100%" height="100%">
    <circle cx="50" cy="50" r="45" fill="#FFD700" stroke="#B8860B" stroke-width="4"/>
    <circle cx="50" cy="50" r="30" fill="none" stroke="#B8860B" stroke-width="2"/>
  </svg>`;
  
  Object.assign(flyingChip.style, {
    position: 'fixed',
    left: `${centerPoint.x - CONFIG.CHIP_SIZE / 2}px`,
    top: `${centerPoint.y - CONFIG.CHIP_SIZE / 2}px`,
    width: `${CONFIG.CHIP_SIZE}px`,
    height: `${CONFIG.CHIP_SIZE}px`,
    zIndex: CONFIG.Z_INDEX.toString(),
    pointerEvents: 'none',
    willChange: 'left, top, transform',  // 🔧 GPU加速
    backfaceVisibility: 'hidden',
    overflow: 'hidden',
    borderRadius: '50%',
    opacity: '0',
    transform: 'translateZ(0)'  // 🔧 强制GPU渲染
  });
  
  document.body.appendChild(flyingChip);
  return flyingChip;
}

/**
 * 计算飞向目标中心点的 left/top 值
 * 确保筹码中心与目标中心重合
 */
function getTargetLeftTop(targetCenter: TargetPoint): { left: string; top: string } {
  return {
    left: `${targetCenter.x - CONFIG.CHIP_SIZE / 2}px`,
    top: `${targetCenter.y - CONFIG.CHIP_SIZE / 2}px`
  };
}

/**
 * 清理所有飞行筹码和动画
 */
function cleanupFlyingChips(): void {
  // 🔧 先杀死所有正在运行的 GSAP 动画
  activeSettlementTweens.forEach(tween => {
    try {
      if (tween && typeof tween.kill === 'function') {
        tween.kill();
      }
    } catch (e) {
      // 忽略错误
    }
  });
  activeSettlementTweens = [];
  
  // 移除所有飞行筹码 DOM
  document.querySelectorAll('.settlement-flying-chip').forEach(chip => chip.remove());
}

/**
 * 🚀 批量清理区域内所有筹码（包括未被收集的）
 * 用于结算动画完成后彻底清理
 */
function destroyAllChipsInAreas(areaIds: string[]): void {
  for (const areaId of areaIds) {
    const areaElement = document.querySelector(`.betting-area[data-area="${areaId}"]`);
    if (!areaElement) continue;
    
    const chipSelectors = [
      '.permanent-chip-container',
      '.permanent-chip',
      '.other-player-permanent-chip',
      '.other-player-chip-container'
    ];
    
    for (const selector of chipSelectors) {
      areaElement.querySelectorAll(selector).forEach(chip => chip.remove());
    }
  }
}

// ============================================
// 🎬 场景1: 庄家通吃
// ============================================

/**
 * 庄家通吃动画
 * 轨迹：有下注区域的筹码 → 庄家中心点 → 销毁
 * 
 * @param areaBets 各区域下注信息（只处理有下注的区域）
 */
export async function animateBankerWinsAll(areaBets: AreaBetInfo[]): Promise<void> {
  // 🚩 检查退出标志
  if (isSettlementExiting) return;
  
  const gsap = await loadGSAP();
  if (!gsap || isSettlementExiting) return;
  
  // 获取庄家中心点
  const bankerCenter = getBankerCenter();
  if (!bankerCenter) {
    console.error('[结算动画] 未找到庄家元素');
    return;
  }
  
  // 🔧 只收集有下注区域的筹码
  const areasWithBets = areaBets.filter(bet => bet.totalBet > 0).map(bet => bet.areaId);
  console.log('[结算动画] 庄家通吃 - 有下注的区域:', areasWithBets);
  
  if (areasWithBets.length === 0) {
    console.log('[结算动画] 没有区域有下注，跳过动画');
    return;
  }
  
  // 收集有下注区域的筹码（已限流）
  const allChips = collectChipsFromAreas(areasWithBets);
  if (allChips.length === 0) {
    console.log('[结算动画] 未收集到筹码，跳过动画');
    // 🚀 即使没有收集到筹码，也要清理区域内所有筹码
    destroyAllChipsInAreas(areasWithBets);
    return;
  }
  
  // 🚀 性能优化：直接移动原始筹码（不创建副本，所有筹码都参与动画）
  const flyingChips = convertToFixedPosition(allChips);
  
  // 计算目标位置
  const targetLeft = bankerCenter.x - CONFIG.CHIP_SIZE / 2;
  const targetTop = bankerCenter.y - CONFIG.CHIP_SIZE / 2;
  
  // 执行动画：所有筹码飞向庄家中心点（带淡出效果）
  // 🎯 动态 stagger：大量筹码时自动缩短间隔，防止超时
  const dynamicStagger = getDynamicStagger(flyingChips.length);
  const tween = gsap.to(flyingChips, {
    left: targetLeft,
    top: targetTop,
    opacity: 0,  // 🔧 飞向庄家时淡出
    duration: CONFIG.DURATION_COLLECT,
    ease: 'power1.inOut',
    stagger: dynamicStagger,
    onComplete: () => {
      // 🚩 检查退出标志
      if (isSettlementExiting) return;
      // 🚀 直接删除原始筹码（已经是fixed定位，直接移除）
      flyingChips.forEach(chip => chip.remove());
    }
  });
  activeSettlementTweens.push(tween);
  await tween;
}

// ============================================
// 🎬 场景2: 庄家通赔
// ============================================

/**
 * 计算区域应获得的筹码数量（基于下注额×倍数）
 * 使用 splitAmountToChips 将金额拆分成标准筹码组合，返回总筹码数
 * 限制最大筹码数为15个，避免动画过于复杂
 * @param minBetLimit 厅的最小下注限制
 */
function calculateChipCount(totalBet: number, multiplier: number, minBetLimit: number = 1): number {
  const payout = totalBet * multiplier;
  // 使用标准筹码拆分算法计算筹码数量（传入厅的最小下注限制）
  const chipCombo = splitAmountToChips(payout, minBetLimit);
  const totalChips = chipCombo.reduce((sum, c) => sum + c.count, 0);
  // 限制最大筹码数为15个，避免动画过于复杂
  return Math.min(15, Math.max(1, totalChips));
}

/**
 * 庄家通赔动画
 * 轨迹：
 * 1. 庄家筹码 → 有下注的区域（数量=下注额×倍数）
 * 2. 300ms后，所有筹码 → 在线玩家 → 销毁
 * 
 * @param areaBets 各区域下注信息（只向有下注的区域分发筹码）
 * @param minBetLimit 厅的最小下注限制
 */
export async function animateBankerLosesAll(areaBets: AreaBetInfo[], minBetLimit: number = 1): Promise<void> {
  // 🚩 检查退出标志
  if (isSettlementExiting) return;
  
  console.log('[结算动画] 🎰 庄家通赔动画开始');
  
  const gsap = await loadGSAP();
  if (!gsap || isSettlementExiting) {
    console.error('[结算动画] GSAP 加载失败或已退出');
    return;
  }
  
  const bankerCenter = getBankerCenter();
  const onlineCenter = getOnlinePlayerCenter();
  
  if (!bankerCenter || !onlineCenter) {
    console.error('[结算动画] 未找到目标元素');
    return;
  }
  
  // 🔧 只处理有下注的区域
  const areasWithBets = areaBets.filter(bet => bet.totalBet > 0);
  console.log('[结算动画] 庄家通赔 - 有下注的区域:', areasWithBets.map(b => b.areaId));
  
  if (areasWithBets.length === 0) {
    console.log('[结算动画] 没有区域有下注，跳过动画');
    return;
  }
  
  // 收集有下注区域的筹码（作为模板）
  const areasWithBetsIds = areasWithBets.map(b => b.areaId);
  const templateChips = collectChipsFromAreas(areasWithBetsIds);
  
  // 为每个有下注的区域计算赔付筹码数量
  const areaChipDistribution: { areaId: string; center: TargetPoint; chipCount: number }[] = [];
  
  for (const bet of areasWithBets) {
    const center = getBettingAreaCenter(bet.areaId);
    if (center) {
      const chipCount = calculateChipCount(bet.totalBet, bet.multiplier, minBetLimit);
      areaChipDistribution.push({ areaId: bet.areaId, center, chipCount });
      console.log(`[结算动画] 区域 ${bet.areaId}: 下注${bet.totalBet} × 倍数${bet.multiplier} = 赔付${bet.totalBet * bet.multiplier}, 筹码数${chipCount}`);
    }
  }
  
  if (areaChipDistribution.length === 0) return;
  
  // 阶段1：从庄家中心创建筹码并飞向各区域
  const flyingChips: HTMLElement[] = [];
  const chipAreaMap: { chip: HTMLElement; targetCenter: TargetPoint }[] = [];
  
  for (const dist of areaChipDistribution) {
    for (let i = 0; i < dist.chipCount; i++) {
      const templateChip = templateChips[i % templateChips.length];
      const chip = templateChip 
        ? createFlyingChipFromCenter(bankerCenter, templateChip)
        : createDefaultFlyingChip(bankerCenter);
      flyingChips.push(chip);
      chipAreaMap.push({ chip, targetCenter: dist.center });
    }
  }
  
  console.log('[结算动画] 创建飞行筹码数量:', flyingChips.length);
  
  await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  
  flyingChips.forEach(chip => {
    chip.style.opacity = '1';
  });
  
  // 🚩 检查退出标志
  if (isSettlementExiting) return;
  
  // 分发筹码到各区域
  // 🎯 动态 stagger：大量筹码时自动缩短间隔，防止超时
  const distributeStagger = getDynamicStagger(chipAreaMap.length);
  const distributeAnimations = chipAreaMap.map(({ chip, targetCenter }, index) => {
    const randomOffsetX = (Math.random() - 0.5) * 30;
    const randomOffsetY = (Math.random() - 0.5) * 20;
    const targetLeft = targetCenter.x - CONFIG.CHIP_SIZE / 2 + randomOffsetX;
    const targetTop = targetCenter.y - CONFIG.CHIP_SIZE / 2 + randomOffsetY;
    
    const tween = gsap.to(chip, {
      left: targetLeft,
      top: targetTop,
      duration: CONFIG.DURATION_DISTRIBUTE,
      ease: 'power2.out',  // 🔧 分发时用渐出缓动，更自然
      delay: index * distributeStagger
    });
    activeSettlementTweens.push(tween);
    return tween;
  });
  
  await Promise.all(distributeAnimations);
  
  // 🚩 检查退出标志
  if (isSettlementExiting) return;
  
  await new Promise(resolve => setTimeout(resolve, CONFIG.DELAY_BEFORE_DESTROY));
  
  // 阶段2：原始筹码 + 飞行筹码 一起飞向在线玩家销毁
  // 🚀 直接移动原始筹码（不克隆）
  const originalFlyingChips = convertToFixedPosition(templateChips);
  
  const allFlyingChipsToDestroy = [...flyingChips, ...originalFlyingChips];
  
  allFlyingChipsToDestroy.forEach(chip => {
    chip.style.zIndex = String(CONFIG.Z_INDEX_DESTROY);
  });
  
  const onlineTargetLeft = onlineCenter.x - CONFIG.CHIP_SIZE / 2;
  const onlineTargetTop = onlineCenter.y - CONFIG.CHIP_SIZE / 2;
  
  // 🚩 检查退出标志
  if (isSettlementExiting) return;
  
  // 🎯 动态 stagger：大量筹码时自动缩短间隔，防止超时
  const destroyStagger = getDynamicStagger(allFlyingChipsToDestroy.length);
  const destroyTween = gsap.to(allFlyingChipsToDestroy, {
    left: onlineTargetLeft,
    top: onlineTargetTop,
    opacity: 0,  // 🔧 飞向在线玩家时淡出隐藏
    duration: CONFIG.DURATION_DESTROY,
    ease: 'power1.inOut',
    stagger: destroyStagger,
    onComplete: () => {
      // 🚩 检查退出标志
      if (isSettlementExiting) return;
      // 🚀 直接删除（已是fixed定位）
      allFlyingChipsToDestroy.forEach(chip => chip.remove());
    }
  });
  activeSettlementTweens.push(destroyTween);
  await destroyTween;
}

// ============================================
// 🎬 场景3: 部分区域赢
// ============================================

/**
 * 部分区域赢动画
 * 轨迹：
 * 1. 有下注的输家区域筹码 → 庄家中心（多对一）
 * 2. 庄家筹码 → 有下注的赢家区域（数量=赢家下注额×倍数）
 * 3. 300ms后，所有筹码 → 在线玩家 → 销毁
 * 
 * @param winningAreas 获胜区域ID数组
 * @param losingAreas 失败区域ID数组
 * @param areaBets 各区域下注信息
 * @param minBetLimit 厅的最小下注限制
 */
export async function animatePartialWin(
  winningAreas: string[],
  losingAreas: string[],
  areaBets: AreaBetInfo[],
  minBetLimit: number = 1
): Promise<void> {
  // 🚩 检查退出标志
  if (isSettlementExiting) return;
  
  const gsap = await loadGSAP();
  if (!gsap || isSettlementExiting) return;
  
  const bankerCenter = getBankerCenter();
  const onlineCenter = getOnlinePlayerCenter();
  
  if (!bankerCenter || !onlineCenter) {
    console.error('[结算动画] 未找到目标元素');
    return;
  }
  
  // 🔧 筛选有下注的输家区域和赢家区域
  const loserBets = areaBets.filter(bet => losingAreas.includes(bet.areaId) && bet.totalBet > 0);
  const winnerBets = areaBets.filter(bet => winningAreas.includes(bet.areaId) && bet.totalBet > 0);
  
  
  const loserAreaIds = loserBets.map(b => b.areaId);
  const winnerAreaIds = winnerBets.map(b => b.areaId);
  
  // 收集有下注的输家和赢家筹码
  const loserChips = collectChipsFromAreas(loserAreaIds);
  const winnerChips = collectChipsFromAreas(winnerAreaIds);
  
  if (loserChips.length === 0 && winnerChips.length === 0) {
    console.log('[结算动画] 没有筹码需要处理，跳过动画');
    // 🚀 清理所有区域的筹码
    destroyAllChipsInAreas([...loserAreaIds, ...winnerAreaIds]);
    return;
  }
  
  // ========== 阶段1：有下注的输家筹码 → 庄家中心（多对一）==========
  let loserFlyingChips: HTMLElement[] = [];
  
  if (loserChips.length > 0) {
    // 🚀 性能优化：直接移动原始筹码（所有筹码都参与动画）
    loserFlyingChips = convertToFixedPosition(loserChips);
    
    const bankerTargetLeft = bankerCenter.x - CONFIG.CHIP_SIZE / 2;
    const bankerTargetTop = bankerCenter.y - CONFIG.CHIP_SIZE / 2;
    
    // 🎯 动态 stagger：大量筹码时自动缩短间隔，防止超时
    const loserStagger = getDynamicStagger(loserFlyingChips.length);
    const loserTween = gsap.to(loserFlyingChips, {
      left: bankerTargetLeft,
      top: bankerTargetTop,
      duration: CONFIG.DURATION_COLLECT,
      ease: 'power1.inOut',
      stagger: loserStagger,
      onComplete: () => {
        // 🚩 检查退出标志
        if (isSettlementExiting) return;
        loserFlyingChips.forEach(chip => {
          chip.style.opacity = '0';
        });
      }
    });
    activeSettlementTweens.push(loserTween);
    await loserTween;
    
    // 🚩 检查退出标志
    if (isSettlementExiting) return;
    
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  // ========== 阶段2：庄家筹码 → 有下注的赢家区域（按倍数分配）==========
  const winnerDistributedChips: HTMLElement[] = [];
  
  if (winnerBets.length > 0) {
    // 计算每个赢家区域应获得的筹码数量
    const winnerChipDistribution: { center: TargetPoint; chipCount: number }[] = [];
    
    for (const bet of winnerBets) {
      const center = getBettingAreaCenter(bet.areaId);
      if (center) {
        const chipCount = calculateChipCount(bet.totalBet, bet.multiplier, minBetLimit);
        winnerChipDistribution.push({ center, chipCount });
        console.log(`[结算动画] 赢家 ${bet.areaId}: 下注${bet.totalBet} × 倍数${bet.multiplier} = 赔付${bet.totalBet * bet.multiplier}, 筹码数${chipCount}`);
      }
    }
    
    // 创建飞向赢家的筹码
    const chipAreaMap: { chip: HTMLElement; targetCenter: TargetPoint }[] = [];
    
    for (const dist of winnerChipDistribution) {
      for (let i = 0; i < dist.chipCount; i++) {
        // 使用输家筹码或赢家筹码作为模板
        const templateChip = loserChips[i % Math.max(1, loserChips.length)] || winnerChips[0];
        const chip = templateChip 
          ? createFlyingChipFromCenter(bankerCenter, templateChip)
          : createDefaultFlyingChip(bankerCenter);
        winnerDistributedChips.push(chip);
        chipAreaMap.push({ chip, targetCenter: dist.center });
      }
    }
    
    if (winnerDistributedChips.length > 0) {
      winnerDistributedChips.forEach(chip => {
        chip.style.opacity = '1';
      });
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 🚩 检查退出标志
      if (isSettlementExiting) return;
      
      // 🎯 动态 stagger：大量筹码时自动缩短间隔，防止超时
      const winnerDistributeStagger = getDynamicStagger(chipAreaMap.length);
      const distributeAnimations = chipAreaMap.map(({ chip, targetCenter }, index) => {
        const randomOffsetX = (Math.random() - 0.5) * 30;
        const randomOffsetY = (Math.random() - 0.5) * 20;
        const targetLeft = targetCenter.x - CONFIG.CHIP_SIZE / 2 + randomOffsetX;
        const targetTop = targetCenter.y - CONFIG.CHIP_SIZE / 2 + randomOffsetY;
        
        const tween = gsap.to(chip, {
          left: targetLeft,
          top: targetTop,
          duration: CONFIG.DURATION_DISTRIBUTE,
          ease: 'power2.out',
          delay: index * winnerDistributeStagger
        });
        activeSettlementTweens.push(tween);
        return tween;
      });
      
      await Promise.all(distributeAnimations);
    }
  }
  
  // 🚩 检查退出标志
  if (isSettlementExiting) return;
  
  await new Promise(resolve => setTimeout(resolve, CONFIG.DELAY_BEFORE_DESTROY));
  
  // ========== 阶段3：所有筹码 → 在线玩家中心（销毁）==========
  const allFlyingChips: HTMLElement[] = [...loserFlyingChips, ...winnerDistributedChips];
  
  // 🚀 赢家区域原始筹码：直接转换为fixed定位（不克隆）
  if (winnerChips.length > 0) {
    const winnerOriginalFlyingChips = convertToFixedPosition(winnerChips);
    allFlyingChips.push(...winnerOriginalFlyingChips);
  }
  
  if (allFlyingChips.length > 0) {
    allFlyingChips.forEach(chip => {
      chip.style.zIndex = String(CONFIG.Z_INDEX_DESTROY);
    });
    
    const onlineTargetLeft = onlineCenter.x - CONFIG.CHIP_SIZE / 2;
    const onlineTargetTop = onlineCenter.y - CONFIG.CHIP_SIZE / 2;
    
    // 🚩 检查退出标志
    if (isSettlementExiting) return;
    
    // 🎯 动态 stagger：大量筹码时自动缩短间隔，防止超时
    const finalDestroyStagger = getDynamicStagger(allFlyingChips.length);
    const destroyTween = gsap.to(allFlyingChips, {
      left: onlineTargetLeft,
      top: onlineTargetTop,
      opacity: 0,  // 🔧 飞向在线玩家时淡出隐藏
      duration: CONFIG.DURATION_DESTROY,
      ease: 'power1.inOut',
      stagger: finalDestroyStagger,
      onComplete: () => {
        // 🚩 检查退出标志
        if (isSettlementExiting) return;
        // 🚀 直接删除（已是fixed定位）
        allFlyingChips.forEach(chip => chip.remove());
      }
    });
    activeSettlementTweens.push(destroyTween);
    await destroyTween;
  }
}

// ============================================
// 🎯 主调度函数
// ============================================

/**
 * 根据开奖结果执行对应的筹码结算动画
 * 
 * @param params 结算参数
 * @param params.winningSectors 获胜区域数组
 * @param params.areaBets 各区域下注信息（包含下注额和倍数）
 * 
 * @example
 * // 庄家通吃（所有区域都输）
 * executeChipSettlement({
 *   winningSectors: [],
 *   areaBets: [
 *     { areaId: 'tian', totalBet: 1000, multiplier: 1 },
 *     { areaId: 'di', totalBet: 500, multiplier: 2 },
 *     { areaId: 'xuan', totalBet: 0, multiplier: 1 },  // 无下注，不参与
 *     { areaId: 'huang', totalBet: 800, multiplier: 1 }
 *   ]
 * });
 * 
 * @example
 * // 庄家通赔（所有区域都赢）
 * executeChipSettlement({
 *   winningSectors: ['tian', 'di', 'xuan', 'huang'],
 *   areaBets: [
 *     { areaId: 'tian', totalBet: 1000, multiplier: 2 },
 *     { areaId: 'di', totalBet: 500, multiplier: 3 },
 *     { areaId: 'xuan', totalBet: 0, multiplier: 1 },  // 无下注，虽然赢了但不分筹码
 *     { areaId: 'huang', totalBet: 800, multiplier: 2 }
 *   ]
 * });
 */
export async function executeChipSettlement(params: SettlementParams): Promise<void> {
  const { winningSectors, areaBets, minBetLimit = 1 } = params;
  const allAreas = ['tian', 'di', 'xuan', 'huang'];
  
  console.log('[结算动画] 🎰 开始执行筹码结算');
  console.log('[结算动画] 获胜区域:', winningSectors);
  console.log('[结算动画] 各区域下注:', areaBets);
  console.log('[结算动画] 最小筹码限制:', minBetLimit);
  
  try {
    if (winningSectors.length === 0) {
      // 场景1: 庄家通吃（无人获胜）
      console.log('[结算动画] 场景: 庄家通吃');
      await animateBankerWinsAll(areaBets);
    } else if (winningSectors.length === 4) {
      // 场景2: 庄家通赔（所有区域获胜）
      console.log('[结算动画] 场景: 庄家通赔');
      await animateBankerLosesAll(areaBets, minBetLimit);
    } else {
      // 场景3: 部分区域赢
      console.log('[结算动画] 场景: 部分区域赢');
      const losingAreas = allAreas.filter(area => !winningSectors.includes(area));
      await animatePartialWin(winningSectors, losingAreas, areaBets, minBetLimit);
    }
    
    console.log('[结算动画] ✅ 筹码结算完成');
  } catch (error) {
    console.error('[结算动画] 执行出错:', error);
    cleanupFlyingChips();
  }
}

/**
 * 清理函数（用于重置或切换房间）
 * 设置退出标志并清理所有动画
 */
export function cleanupSettlementChips(): void {
  // 🚩 设置退出标志，阻止动画回调继续执行
  isSettlementExiting = true;
  
  // 清理所有飞行筹码和动画
  cleanupFlyingChips();
  
  console.log('[结算动画] 已清理所有结算动画');
}

/**
 * 重置退出标志（进入新房间时调用）
 */
export function resetSettlementState(): void {
  isSettlementExiting = false;
  activeSettlementTweens = [];
}
